import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';

import { CategoryNavigator, useDebounce } from '@/exceed';

import styles from '../styles/enrollmentsSearch.module.scss';

const EnrollmentsSearch = ({
  enrollmentsFilters,
  enrolledUsersQuery,
  setEnrolledUsersQuery,
  isModalVariant = false,
  resetEnrollmentsSelectedUsers,
}) => {
  const [shouldShowFilters, setShouldShowFilters] = useState(false);
  const [searchValue, setSearchValue] = useState(enrolledUsersQuery.filters.user.query || '');

  const filtersKey = isModalVariant ? 'user' : 'enrollment';
  const queryAsString = JSON.stringify(enrolledUsersQuery);

  const hasActiveFilters = useMemo(() => {
    const { filters } = enrolledUsersQuery;
    const { query, ...copyOfUser } = filters.user;
    const copyOfFilters = { ...filters, user: copyOfUser };
    return Object.keys(copyOfFilters[filtersKey]).length > 0;
  }, [queryAsString, filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSetEnrolledUsersQuery = useDebounce(setEnrolledUsersQuery, 200);

  const onSearchInputChange = (updatedSearchValue) => {
    setSearchValue(updatedSearchValue);
    resetEnrollmentsSelectedUsers();
    debouncedSetEnrolledUsersQuery((oldQuery) => ({
      ...oldQuery,
      filters: {
        ...oldQuery.filters,
        user: { ...oldQuery.filters.user, query: updatedSearchValue },
      },
      pagination_params: { ...oldQuery.pagination_params, page: 1 },
    }));
  };

  const onSearchInputFocus = () => {
    if (hasActiveFilters) return;
    setShouldShowFilters(true);
  };

  const onCategoryNavigatorBlur = () => {
    if (hasActiveFilters || enrolledUsersQuery.filters.user.query) return;
    setShouldShowFilters(false);
  };

  const onClearSearchClick = () => {
    onSearchInputChange('');
  };

  const onFilterClick = ({ key, value, isSingleSelect, isToggle }) => {
    const copyOfFilters = { ...enrolledUsersQuery.filters[filtersKey] };

    if (isSingleSelect) {
      if (copyOfFilters[key] === value) {
        delete copyOfFilters[key];
      } else {
        copyOfFilters[key] = value === false && isToggle ? true : value;
      }
    } else {
      const existingValues = copyOfFilters[key] || [];
      const existingValueIndex = existingValues.indexOf(value);

      if (existingValueIndex !== -1) {
        existingValues.splice(existingValueIndex, 1);
      } else {
        existingValues.push(value);
      }

      if (!existingValues.length) {
        delete copyOfFilters[key];
      } else {
        copyOfFilters[key] = existingValues;
      }
    }

    resetEnrollmentsSelectedUsers();

    const copyOfQuery = { ...enrolledUsersQuery };
    copyOfQuery.filters[filtersKey] = copyOfFilters;

    setEnrolledUsersQuery(() => copyOfQuery);
  };

  const onClearFiltersClick = () => {
    setEnrolledUsersQuery((oldQuery) => ({
      ...oldQuery,
      filters: { enrollment: {}, user: { query: oldQuery.filters.user.query } },
    }));
    resetEnrollmentsSelectedUsers();
  };

  const shouldShowFiltersFunc = () =>
    hasActiveFilters || Boolean(enrolledUsersQuery.filters.user.query) || shouldShowFilters;

  const searchPlaceholder = isModalVariant ? i18n.t('search-users') : i18n.t('search-enrollments');

  return (
    <CategoryNavigator
      className={styles.element}
      filters={enrollmentsFilters}
      onCategoryNavigatorBlur={onCategoryNavigatorBlur}
      onClearFiltersClick={onClearFiltersClick}
      onClearSearchClick={onClearSearchClick}
      onFilterClick={onFilterClick}
      onSearchInputChange={onSearchInputChange}
      onSearchInputFocus={onSearchInputFocus}
      searchPlaceholder={searchPlaceholder}
      searchValue={searchValue}
      selectedFilterValues={enrolledUsersQuery.filters[filtersKey]}
      shouldAnimateFilters
      shouldShowClearFilters={hasActiveFilters}
      shouldShowFilters={shouldShowFiltersFunc()}
      shouldShowSelect={false}
      testId="enrollments-search"
    />
  );
};

EnrollmentsSearch.propTypes = {
  enrolledUsersQuery: PropTypes.shape({}).isRequired,
  enrollmentsFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setEnrolledUsersQuery: PropTypes.func.isRequired,
  isModalVariant: PropTypes.bool,
};

export default EnrollmentsSearch;
